import * as THREE from 'three'

export default class IntroSection
{
    constructor(_options)
    {
        // Options
        this.config = _options.config
        this.time = _options.time
        this.resources = _options.resources
        this.objects = _options.objects
        this.areas = _options.areas
        this.zones = _options.zones
        this.camera = _options.camera
        this.passes = _options.passes
        this.walls = _options.walls
        this.tiles = _options.tiles
        this.debug = _options.debug
        this.x = _options.x
        this.y = _options.y
        this.car = _options.car
        this.shadows = _options.shadows
        this.sounds = _options.sounds

        // Set up
        this.container = new THREE.Object3D()
        this.container.matrixAutoUpdate = false
        this.container.updateMatrix()

        // Collectible system
        this.collectibleCounter = 0
        this.collectibleTargets = []
        this.collectibleThreshold = 1.3
        this.collectibleRewardOpened = false

        this.setStatic()
        this.setInstructions()
        this.setOtherInstructions()
        this.setTitles()
        this.setPapers()
        this.setTiles()
        this.setDikes()
        this.setZone()
        this.setBowling()
        this.setCollectibles()
        this.setTexts()
    }

    setStatic()
    {
        // Preserve GLB materials only for meshes without shade/pure/floor parser names
        this.objects.add({
            base: this.resources.items.introStaticBase.scene,
            collision: this.resources.items.introStaticCollision.scene,
            floorShadowTexture: this.resources.items.introStaticFloorShadowTexture,
            animations: this.resources.items.introStaticBase.animations,
            offset: new THREE.Vector3(0, 0, 0),
            mass: 0,
            preserveUnmatched: true
        })
    }

    setInstructions()
    {
        this.instructions = {}

        /**
         * Arrows
         */
        this.instructions.arrows = {}

        // Label
        this.instructions.arrows.label = {}

        this.instructions.arrows.label.texture = this.config.touch ? this.resources.items.introInstructionsControlsTexture : this.resources.items.introInstructionsArrowsTexture
        this.instructions.arrows.label.texture.magFilter = THREE.NearestFilter
        this.instructions.arrows.label.texture.minFilter = THREE.LinearFilter

        this.instructions.arrows.label.material = new THREE.MeshBasicMaterial({ transparent: true, alphaMap: this.instructions.arrows.label.texture, color: 0x000000, depthWrite: false, opacity: 0 })

        this.instructions.arrows.label.geometry = this.resources.items.introInstructionsLabels.scene.children.find((_mesh) => _mesh.name === 'arrows').geometry

        this.instructions.arrows.label.mesh = new THREE.Mesh(this.instructions.arrows.label.geometry, this.instructions.arrows.label.material)
        this.container.add(this.instructions.arrows.label.mesh)

        if(!this.config.touch)
        {
            // Keys
            this.instructions.arrows.up = this.objects.add({
                base: this.resources.items.introArrowKeyBase.scene,
                collision: this.resources.items.introArrowKeyCollision.scene,
                offset: new THREE.Vector3(0, 0, 0),
                rotation: new THREE.Euler(0, 0, 0),
                duplicated: true,
                shadow: { sizeX: 1, sizeY: 1, offsetZ: - 0.2, alpha: 0.5 },
                mass: 1.5,
                soundName: 'brick'
            })
            this.instructions.arrows.down = this.objects.add({
                base: this.resources.items.introArrowKeyBase.scene,
                collision: this.resources.items.introArrowKeyCollision.scene,
                offset: new THREE.Vector3(0, - 0.8, 0),
                rotation: new THREE.Euler(0, 0, Math.PI),
                duplicated: true,
                shadow: { sizeX: 1, sizeY: 1, offsetZ: - 0.2, alpha: 0.5 },
                mass: 1.5,
                soundName: 'brick'
            })
            this.instructions.arrows.left = this.objects.add({
                base: this.resources.items.introArrowKeyBase.scene,
                collision: this.resources.items.introArrowKeyCollision.scene,
                offset: new THREE.Vector3(- 0.8, - 0.8, 0),
                rotation: new THREE.Euler(0, 0, Math.PI * 0.5),
                duplicated: true,
                shadow: { sizeX: 1, sizeY: 1, offsetZ: - 0.2, alpha: 0.5 },
                mass: 1.5,
                soundName: 'brick'
            })
            this.instructions.arrows.right = this.objects.add({
                base: this.resources.items.introArrowKeyBase.scene,
                collision: this.resources.items.introArrowKeyCollision.scene,
                offset: new THREE.Vector3(0.8, - 0.8, 0),
                rotation: new THREE.Euler(0, 0, - Math.PI * 0.5),
                duplicated: true,
                shadow: { sizeX: 1, sizeY: 1, offsetZ: - 0.2, alpha: 0.5 },
                mass: 1.5,
                soundName: 'brick'
            })
        }
    }

    setOtherInstructions()
    {
        if(this.config.touch)
        {
            return
        }


    }

    setTitles()
    {
        // EDIT HERE: Replace this entire method starting at line 160
        // Replace the BRUNO SIMON letters with MOHAMMED AL KHAYYAT
        // Use math-based positioning with proper spacing
        // Title
        this.objects.add({
            base: this.resources.items.introMBase.scene,
            collision: this.resources.items.introMCollision.scene,
            offset: new THREE.Vector3(-9, 3, 0),
            rotation: new THREE.Euler(0, 0, 0),
            shadow: { sizeX: 1.5, sizeY: 1.5, offsetZ: - 0.6, alpha: 0.4 },
            duplicated: true,
            mass: 1.5,
            soundName: 'brick'
        })
        this.objects.add({
            base: this.resources.items.introOBase.scene,
            collision: this.resources.items.introOCollision.scene,
            offset: new THREE.Vector3(-5, 3, 0),
            rotation: new THREE.Euler(0, 0, 0),
            shadow: { sizeX: 1.5, sizeY: 1.5, offsetZ: - 0.6, alpha: 0.4 },
            mass: 1.5,
            soundName: 'brick'
        })
        this.objects.add({
            base: this.resources.items.introHBase.scene,
            collision: this.resources.items.introHCollision.scene,
            offset: new THREE.Vector3(-7, 3, 0),
            rotation: new THREE.Euler(0, 0, 0),
            shadow: { sizeX: 1.5, sizeY: 1.5, offsetZ: - 0.6, alpha: 0.4 },
            duplicated: true,
            mass: 1.5,
            soundName: 'brick'
        })
        this.objects.add({
            base: this.resources.items.introABase.scene,
            collision: this.resources.items.introACollision.scene,
            offset: new THREE.Vector3(-6, 3, 0),
            rotation: new THREE.Euler(0, 0, 0),
            shadow: { sizeX: 1.5, sizeY: 1.5, offsetZ: - 0.6, alpha: 0.4 },
            duplicated: true,
            mass: 1.5,
            soundName: 'brick'
        })
        this.objects.add({
            base: this.resources.items.introMBase.scene,
            collision: this.resources.items.introMCollision.scene,
            offset: new THREE.Vector3(-5, 3, 0),
            rotation: new THREE.Euler(0, 0, 0),
            shadow: { sizeX: 1.5, sizeY: 1.5, offsetZ: - 0.6, alpha: 0.4 },
            duplicated: true,
            mass: 1.5,
            soundName: 'brick'
        })
        this.objects.add({
            base: this.resources.items.introMBase.scene,
            collision: this.resources.items.introMCollision.scene,
            offset: new THREE.Vector3(-4, 3, 0),
            rotation: new THREE.Euler(0, 0, 0),
            shadow: { sizeX: 1.5, sizeY: 1.5, offsetZ: - 0.6, alpha: 0.4 },
            duplicated: true,
            mass: 1.5,
            soundName: 'brick'
        })
        this.objects.add({
            base: this.resources.items.introEBase.scene,
            collision: this.resources.items.introECollision.scene,
            offset: new THREE.Vector3(-3, 3, 0),
            rotation: new THREE.Euler(0, 0, 0),
            shadow: { sizeX: 1.5, sizeY: 1.5, offsetZ: - 0.6, alpha: 0.4 },
            duplicated: true,
            mass: 1.5,
            soundName: 'brick'
        })
        this.objects.add({
            base: this.resources.items.introDBase.scene,
            collision: this.resources.items.introDCollision.scene,
            offset: new THREE.Vector3(-2.3, 3, 0),
            rotation: new THREE.Euler(0, 0, 0),
            shadow: { sizeX: 1.5, sizeY: 1.5, offsetZ: - 0.6, alpha: 0.4 },
            mass: 1.5,
            soundName: 'brick'
        })
        this.objects.add({
            base: this.resources.items.introABase.scene,
            collision: this.resources.items.introACollision.scene,
            offset: new THREE.Vector3(-0.5, 3, 0),
            rotation: new THREE.Euler(0, 0, 0),
            shadow: { sizeX: 1.5, sizeY: 1.5, offsetZ: - 0.6, alpha: 0.4 },
            duplicated: true,
            mass: 1.5,
            soundName: 'brick'
        })
        this.objects.add({
            base: this.resources.items.introLBase.scene,
            collision: this.resources.items.introLCollision.scene,
            offset: new THREE.Vector3(0.4, 3, 0),
            rotation: new THREE.Euler(0, 0, 0),
            shadow: { sizeX: 1.5, sizeY: 1.5, offsetZ: - 0.6, alpha: 0.4 },
            duplicated: true,
            mass: 1.5,
            soundName: 'brick'
        })
        this.objects.add({
            base: this.resources.items.introKBase.scene,
            collision: this.resources.items.introKCollision.scene,
            offset: new THREE.Vector3(1.3, 3, 0),
            rotation: new THREE.Euler(0, 0, 0),
            shadow: { sizeX: 1.5, sizeY: 1.5, offsetZ: - 0.6, alpha: 0.4 },
            duplicated: true,
            mass: 1.5,
            soundName: 'brick'
        })
        this.objects.add({
            base: this.resources.items.introHBase.scene,
            collision: this.resources.items.introHCollision.scene,
            offset: new THREE.Vector3(2.3, 3, 0),
            rotation: new THREE.Euler(0, 0, 0),
            shadow: { sizeX: 1.5, sizeY: 1.5, offsetZ: - 0.6, alpha: 0.4 },
            duplicated: true,
            mass: 1.5,
            soundName: 'brick'
        })
        this.objects.add({
            base: this.resources.items.introABase.scene,
            collision: this.resources.items.introACollision.scene,
            offset: new THREE.Vector3(3.4, 3, 0),
            rotation: new THREE.Euler(0, 0, 0),
            shadow: { sizeX: 1.5, sizeY: 1.5, offsetZ: - 0.6, alpha: 0.4 },
            duplicated: true,
            mass: 1.5,
            soundName: 'brick'
        })
        this.objects.add({
            base: this.resources.items.introYBase.scene,
            collision: this.resources.items.introYCollision.scene,
            offset: new THREE.Vector3(4.3, 3, 0),
            rotation: new THREE.Euler(0, 0, 0),
            shadow: { sizeX: 1.5, sizeY: 1.5, offsetZ: - 0.6, alpha: 0.4 },
            duplicated: true,
            mass: 1.5,
            soundName: 'brick'
        })
        this.objects.add({
            base: this.resources.items.introYBase.scene,
            collision: this.resources.items.introYCollision.scene,
            offset: new THREE.Vector3(5.2, 3, 0),
            rotation: new THREE.Euler(0, 0, 0),
            shadow: { sizeX: 1.5, sizeY: 1.5, offsetZ: - 0.6, alpha: 0.4 },
            duplicated: true,
            mass: 1.5,
            soundName: 'brick'
        })
        this.objects.add({
            base: this.resources.items.introABase.scene,
            collision: this.resources.items.introACollision.scene,
            offset: new THREE.Vector3(6.1, 3, 0),
            rotation: new THREE.Euler(0, 0, 0),
            shadow: { sizeX: 1.5, sizeY: 1.5, offsetZ: - 0.6, alpha: 0.4 },
            duplicated: true,
            mass: 1.5,
            soundName: 'brick'
        })
        this.objects.add({
            base: this.resources.items.introTBase.scene,
            collision: this.resources.items.introTCollision.scene,
            offset: new THREE.Vector3(7.0, 3, 0),
            rotation: new THREE.Euler(0, 0, 0),
            shadow: { sizeX: 1.5, sizeY: 1.5, offsetZ: - 0.6, alpha: 0.4 },
            duplicated: true,
            mass: 1.5,
            soundName: 'brick'
        })
        this.objects.add({
            base: this.resources.items.introCreativeBase.scene,
            collision: this.resources.items.introCreativeCollision.scene,
            offset: new THREE.Vector3(13, 0, 0),
            rotation: new THREE.Euler(0, 0, 0.25*4),
            shadow: { sizeX: 1.5, sizeY: 1.5, offsetZ: - 0.6, alpha: 0.4 },
            duplicated: true,
            mass: 1.5,
            soundName: 'brick'
        })
        this.objects.add({
            base: this.resources.items.introCreativeBase2.scene,
            collision: this.resources.items.introCreativeCollision.scene,
            offset: new THREE.Vector3(13, 0, -0.2),
            rotation: new THREE.Euler(0, 0, 0),
            shadow: { sizeX: 1.5, sizeY: 1.5, offsetZ: - 0.6, alpha: 0.4 },
            duplicated: true,
            mass: 1.5,
            soundName: 'brick'
        })

    }

    setPapers()
    {
        this.papers = {}

        // Paper 1
        this.papers.paper1 = {}
        this.papers.paper1.geometry = new THREE.PlaneGeometry(4, 3, 1, 1)
        this.papers.paper1.texture = this.resources.items.paper1Texture
        this.papers.paper1.texture.magFilter = THREE.NearestFilter
        this.papers.paper1.texture.minFilter = THREE.LinearFilter
        this.papers.paper1.material = new THREE.MeshBasicMaterial({ 
            transparent: true, 
            alphaMap: this.papers.paper1.texture, 
            color: 0xffffff, 
            depthWrite: false, 
            opacity: 0.9 
        })
        this.papers.paper1.mesh = new THREE.Mesh(this.papers.paper1.geometry, this.papers.paper1.material)
        this.papers.paper1.mesh.position.set(33.22, -14.38, 0.005)
        this.papers.paper1.mesh.rotation.set(0, 0, 0.0)
        this.container.add(this.papers.paper1.mesh)

        // Add clickable area for paper1
        this.papers.paper1.area = this.areas.add({
            position: new THREE.Vector2(33.22, -14.38),
            halfExtents: new THREE.Vector2(2, 1.5) // Half the size of the paper
        })
        this.papers.paper1.area.on('interact', () => {
            window.open('https://pubs.acs.org/doi/abs/10.1021/acs.iecr.4c02637', '_blank')
        })

        // Waves
        this.papers.waves = {}
        this.papers.waves.geometry = new THREE.PlaneGeometry(3.5, 3, 1, 1)
        this.papers.waves.texture = this.resources.items.wavesTexture
        this.papers.waves.texture.magFilter = THREE.NearestFilter
        this.papers.waves.texture.minFilter = THREE.LinearFilter
        this.papers.waves.material = new THREE.MeshBasicMaterial({ 
            transparent: true, 
            alphaMap: this.papers.waves.texture, 
            color: 0xffffff, 
            depthWrite: false, 
            opacity: 0.8 
        })
        this.papers.waves.mesh = new THREE.Mesh(this.papers.waves.geometry, this.papers.waves.material)
        this.papers.waves.mesh.position.set(33.22, -24.45, 0.005)
        this.papers.waves.mesh.rotation.set(0, 0, 0)
        this.container.add(this.papers.waves.mesh)

        // Add clickable area for waves
        this.papers.waves.area = this.areas.add({
            position: new THREE.Vector2(33.22, -24.45),
            halfExtents: new THREE.Vector2(2, 1.5) // Half the size of the waves
        })
        this.papers.waves.area.on('interact', () => {
            window.open('https://doi.org/10.1016/j.cej.2025.168246', '_blank')
        })

        // Google Scholar
        this.papers.googlescholar = {}
        this.papers.googlescholar.geometry = new THREE.PlaneGeometry(4, 3, 1, 1)
        this.papers.googlescholar.texture = this.resources.items.googlescholarTexture
        this.papers.googlescholar.texture.magFilter = THREE.NearestFilter
        this.papers.googlescholar.texture.minFilter = THREE.LinearFilter
        // Custom shader for Google Scholar - make black transparent, show colors
        this.papers.googlescholar.material = new THREE.ShaderMaterial({
            uniforms: {
                uTexture: { value: this.papers.googlescholar.texture },
                uOpacity: { value: 0.9 }
            },
            vertexShader: `
                varying vec2 vUv;
                void main() {
                    vUv = uv;
                    gl_Position = projectionMatrix * modelViewMatrix * vec4(position, 1.0);
                }
            `,
            fragmentShader: `
                uniform sampler2D uTexture;
                uniform float uOpacity;
                varying vec2 vUv;
                
                void main() {
                    vec4 textureColor = texture2D(uTexture, vUv);
                    
                    // Detect black pixels (very low RGB values)
                    float isBlack = step(textureColor.r + textureColor.g + textureColor.b, 0.15);
                    
                    // Show original colors, make black transparent
                    vec3 finalColor = textureColor.rgb;
                    float finalAlpha = (1.0 - isBlack) * uOpacity;
                    
                    gl_FragColor = vec4(finalColor, finalAlpha);
                }
            `,
            transparent: true,
            depthWrite: false
        })
        this.papers.googlescholar.mesh = new THREE.Mesh(this.papers.googlescholar.geometry, this.papers.googlescholar.material)
        this.papers.googlescholar.mesh.position.set(19.8, -43.67, 0.005)
        this.papers.googlescholar.mesh.rotation.set(0, 0, 0)
        this.container.add(this.papers.googlescholar.mesh)

        // Add clickable area for Google Scholar
        this.papers.googlescholar.area = this.areas.add({
            position: new THREE.Vector2(19.8, -43.67),
            halfExtents: new THREE.Vector2(2, 1.5)
        })
        this.papers.googlescholar.area.on('interact', () => {
            window.open('https://scholar.google.com/citations?user=Ngr9V3QAAAAJ&hl=en', '_blank')
        })

        // Degree
        this.papers.degree = {}
        this.papers.degree.geometry = new THREE.PlaneGeometry(4, 3, 1, 1)
        this.papers.degree.texture = this.resources.items.degreeTexture
        this.papers.degree.texture.magFilter = THREE.NearestFilter
        this.papers.degree.texture.minFilter = THREE.LinearFilter
        
        this.papers.degree.material = new THREE.MeshBasicMaterial({ 
            transparent: true, 
            alphaMap: this.papers.degree.texture, 
            color: 0xffffff, 
            depthWrite: false, 
            opacity: 0.9 
        })
        
        this.papers.degree.mesh = new THREE.Mesh(this.papers.degree.geometry, this.papers.degree.material)
        this.papers.degree.mesh.position.set(33.69, -5.31, 0.005)
        this.papers.degree.mesh.rotation.set(0, 0, 0)
        this.container.add(this.papers.degree.mesh)

        // Paper 3
        this.papers.paper3 = {}
        this.papers.paper3.geometry = new THREE.PlaneGeometry(4, 3, 1, 1)
        this.papers.paper3.texture = this.resources.items.paper3Texture
        this.papers.paper3.texture.magFilter = THREE.NearestFilter
        this.papers.paper3.texture.minFilter = THREE.LinearFilter
        this.papers.paper3.material = new THREE.MeshBasicMaterial({ 
            transparent: true, 
            alphaMap: this.papers.paper3.texture, 
            color: 0xffffff, 
            depthWrite: false, 
            opacity: 0.9 
        })
        this.papers.paper3.mesh = new THREE.Mesh(this.papers.paper3.geometry, this.papers.paper3.material)
        this.papers.paper3.mesh.position.set(33.22, -34.45, 0.005)
        this.papers.paper3.mesh.rotation.set(0, 0, 0)
        this.container.add(this.papers.paper3.mesh)

        // Paper 4
        this.papers.paper4 = {}
        this.papers.paper4.geometry = new THREE.PlaneGeometry(4, 3, 1, 1)
        this.papers.paper4.texture = this.resources.items.paper4Texture
        this.papers.paper4.texture.magFilter = THREE.NearestFilter
        this.papers.paper4.texture.minFilter = THREE.LinearFilter
        this.papers.paper4.material = new THREE.ShaderMaterial({
            uniforms: {
                uTexture: { value: this.papers.paper4.texture },
                uOpacity: { value: 0.9 }
            },
            vertexShader: `
                varying vec2 vUv;
                void main() {
                    vUv = uv;
                    gl_Position = projectionMatrix * modelViewMatrix * vec4(position, 1.0);
                }
            `,
            fragmentShader: `
                uniform sampler2D uTexture;
                uniform float uOpacity;
                varying vec2 vUv;
                
                void main() {
                    vec4 textureColor = texture2D(uTexture, vUv);
                    
                    // Detect black pixels (very low RGB values)
                    float isBlack = step(textureColor.r + textureColor.g + textureColor.b, 0.15);
                    
                    // Show original colors, make black transparent
                    vec3 finalColor = textureColor.rgb;
                    float finalAlpha = (1.0 - isBlack) * uOpacity;
                    
                    gl_FragColor = vec4(finalColor, finalAlpha);
                }
            `,
            transparent: true,
            depthWrite: false
        })
        this.papers.paper4.mesh = new THREE.Mesh(this.papers.paper4.geometry, this.papers.paper4.material)
        this.papers.paper4.mesh.position.set(28.27, -43.67, 0.005)
        this.papers.paper4.mesh.rotation.set(0, 0, 0)
        this.container.add(this.papers.paper4.mesh)
        this.papers.paper4.area = this.areas.add({
            position: new THREE.Vector2(28.27, -43.67),
            halfExtents: new THREE.Vector2(2, 1.5)
        })
        this.papers.paper4.area.on('interact', () => {
            window.open('https://www.linkedin.com/in/mo97/', '_blank')
        })

        // Paper 5
        this.papers.paper5 = {}
        this.papers.paper5.geometry = new THREE.PlaneGeometry(4, 3, 1, 1)
        this.papers.paper5.texture = this.resources.items.paper5Texture
        this.papers.paper5.texture.magFilter = THREE.NearestFilter
        this.papers.paper5.texture.minFilter = THREE.LinearFilter
        this.papers.paper5.material = new THREE.MeshBasicMaterial({ 
            transparent: true, 
            alphaMap: this.papers.paper5.texture, 
            color: 0xffffff, 
            depthWrite: false, 
            opacity: 0.9 
        })
        this.papers.paper5.mesh = new THREE.Mesh(this.papers.paper5.geometry, this.papers.paper5.material)
        this.papers.paper5.mesh.position.set(-48.65, -39.65, 0.005)
        this.papers.paper5.mesh.rotation.set(0, 0, 0)
        this.container.add(this.papers.paper5.mesh)

        // Paper 6
        this.papers.paper6 = {}
        this.papers.paper6.geometry = new THREE.PlaneGeometry(4, 3, 1, 1)
        this.papers.paper6.texture = this.resources.items.paper6Texture
        this.papers.paper6.texture.magFilter = THREE.NearestFilter
        this.papers.paper6.texture.minFilter = THREE.LinearFilter
        this.papers.paper6.material = new THREE.MeshBasicMaterial({ 
            transparent: true, 
            alphaMap: this.papers.paper6.texture, 
            color: 0xffffff, 
            depthWrite: false, 
            opacity: 0.9 
        })
        this.papers.paper6.mesh = new THREE.Mesh(this.papers.paper6.geometry, this.papers.paper6.material)
        this.papers.paper6.mesh.position.set(36.45, -43.67, 0.005)
        this.papers.paper6.mesh.rotation.set(0, 0, 0)
        this.container.add(this.papers.paper6.mesh)

        // Paper 7
        this.papers.paper7 = {}
        this.papers.paper7.geometry = new THREE.PlaneGeometry(8, 6, 1, 1)
        this.papers.paper7.texture = this.resources.items.paper7Texture
        this.papers.paper7.texture.magFilter = THREE.NearestFilter
        this.papers.paper7.texture.minFilter = THREE.LinearFilter
        this.papers.paper7.material = new THREE.MeshBasicMaterial({ 
            transparent: true, 
            alphaMap: this.papers.paper7.texture, 
            color: 0xffffff, 
            depthWrite: false, 
            opacity: 0.9 
        })
        this.papers.paper7.mesh = new THREE.Mesh(this.papers.paper7.geometry, this.papers.paper7.material)
        // Keep a small z offset for reuse, but don't place the original paper7 in the scene
        this.papers.paper7.mesh.position.set(13.90, -29.30, 0.005)
        this.papers.paper7.mesh.rotation.set(0, 0, 0)
        // this.container.add(this.papers.paper7.mesh)
    }

    setTiles()
    {
        this.tiles.add({
            start: new THREE.Vector2(2, - 9.5),
            delta: new THREE.Vector2(0, - 20.5)
        })
    }

    setDikes()
    {
        this.dikes = {}
        this.dikes.brickOptions = {
            base: this.resources.items.brickBase.scene,
            collision: this.resources.items.brickCollision.scene,
            offset: new THREE.Vector3(0, 0, 0.1),
            rotation: new THREE.Euler(0, 0, Math.PI * 0.5),
            duplicated: true,
            shadow: { sizeX: 1.2, sizeY: 1.8, offsetZ: - 0.15, alpha: 0.35 },
            mass: 0.5,
            soundName: 'brick',
            preserveMaterials: true
        }

        // this.walls.add({
        //     object:
        //     {
        //         ...this.dikes.brickOptions,
        //         rotation: new THREE.Euler(0, 0, Math.PI * 0.5)
        //     },
        //     shape:
        //     {
        //         type: 'brick',
        //         equilibrateLastLine: true,
        //         widthCount: 3,
        //         heightCount: 2,
        //         position: new THREE.Vector3(this.x + 0, this.y - 4, 0),
        //         offsetWidth: new THREE.Vector3(1.05, 0, 0),
        //         offsetHeight: new THREE.Vector3(0, 0, 0.45),
        //         randomOffset: new THREE.Vector3(0, 0, 0),
        //         randomRotation: new THREE.Vector3(0, 0, 0.2)
        //     }
        // })

        this.walls.add({
            object: {
                ...this.dikes.brickOptions,
                rotation: new THREE.Euler(0, 0, Math.PI * 0.5)
            },
            shape:
            {
                type: 'brick',
                equilibrateLastLine: true,
                widthCount: 2,
                heightCount: 5,
                position: new THREE.Vector3(this.x - 12, this.y - 13, 0),
                offsetWidth: new THREE.Vector3(0, 1.05, 0),
                offsetHeight: new THREE.Vector3(0, 0, 0.9),
                randomOffset: new THREE.Vector3(0, 0, 0),
                randomRotation: new THREE.Vector3(0, 0, 1)
            }
        })

        this.walls.add({
            object: {
                ...this.dikes.brickOptions,
                rotation: new THREE.Euler(0, 0, Math.PI * 0.5)
            },
            shape:
            {
                type: 'brick',
                equilibrateLastLine: true,
                widthCount: 6,
                heightCount: 8,
                position: new THREE.Vector3(this.x - 30, this.y - 47, 0),
                offsetWidth: new THREE.Vector3(0, 1.05, 0),
                offsetHeight: new THREE.Vector3(0, 0, 0.9),
                randomOffset: new THREE.Vector3(0, 0, 0),
                randomRotation: new THREE.Vector3(0, 0, 1)
            }
        })

        // Brick wall using base-2
        this.walls.add({
            object: {
                base: this.resources.items.brickBase3.scene,
                collision: this.resources.items.brickCollision.scene,
                offset: new THREE.Vector3(0, 0, 0.1),
                rotation: new THREE.Euler(0, 0, Math.PI * 0.5),
                duplicated: true,
                shadow: { sizeX: 1.2, sizeY: 1.8, offsetZ: - 0.15, alpha: 0.35 },
                mass: 0.5,
                soundName: 'brick',
                preserveMaterials: true
            },
            shape:
            {
                type: 'brick',
                equilibrateLastLine: true,
                widthCount: 2,
                heightCount: 2,
                position: new THREE.Vector3(this.x - 34.5, this.y - 30, 2.1),
                offsetWidth: new THREE.Vector3(0, 1.05, 0),
                offsetHeight: new THREE.Vector3(0, 0, 0.9),
                randomOffset: new THREE.Vector3(0, 0, 0),
                randomRotation: new THREE.Vector3(0, 0, 1)
            }
        })
    }

    setZone()
    {
        // Track if we're in any papers zone
        let papersZoneCount = 0

        // Rectangle zone from (17.54, 6.87) to (50.63, -42.56)
        const zone = this.zones.add({
            position: { x: this.x + 34.085, y: this.y - 17.845 },
            halfExtents: { x: 16.545, y: 24.715 },
            data: { cameraAngle: 'papers' }
        })

        zone.on('in', (_data) =>
        {
            papersZoneCount++
            this.camera.angle.set(_data.cameraAngle)
        })

        zone.on('out', () =>
        {
            papersZoneCount--
            if(papersZoneCount === 0)
            {
                this.camera.angle.set('default')
            }
        })

        // Zone at paper7 location
        const zone2 = this.zones.add({
            position: { x: this.x + 13.90, y: this.y - 29.30 },
            halfExtents: { x: 6, y: 6 },
            data: { cameraAngle: 'papers' }
        })

        zone2.on('in', (_data) =>
        {
            papersZoneCount++
            this.camera.angle.set(_data.cameraAngle)
        })

        zone2.on('out', () =>
        {
            papersZoneCount--
            if(papersZoneCount === 0)
            {
                this.camera.angle.set('default')
            }
        })
    }

    setBowling()
    {
        this.bowling = {}
        this.bowling.x = -33
        this.bowling.y = -20

        // Pins at y: -20 (rotated to face ball at y: -33)
        this.bowling.pins = this.walls.add({
            object:
            {
                base: this.resources.items.bowlingPinBase.scene,
                collision: this.resources.items.bowlingPinCollision.scene,
                offset: new THREE.Vector3(0, 0, 0.1),
                rotation: new THREE.Euler(0, 0, 0),
                duplicated: true,
                shadow: { sizeX: 1.4, sizeY: 1.4, offsetZ: - 0.15, alpha: 0.35 },
                mass: 0.1,
                soundName: 'bowlingPin'
            },
            shape:
            {
                type: 'triangle',
                widthCount: 4,
                position: new THREE.Vector3(this.bowling.x, this.bowling.y, 0),
                offsetWidth: new THREE.Vector3(1, 0, 0),
                offsetHeight: new THREE.Vector3(0, -0.65, 0),
                randomOffset: new THREE.Vector3(0, 0, 0),
                randomRotation: new THREE.Vector3(0, 0, 0)
            }
        })

        // Ball at y: -33 (aligned x with pins)
        this.bowling.ball = this.objects.add({
            base: this.resources.items.bowlingBallBase.scene,
            collision: this.resources.items.bowlingBallCollision.scene,
            offset: new THREE.Vector3(this.bowling.x, -33, 0),
            rotation: new THREE.Euler(Math.PI * 0.5, 0, 0),
            duplicated: true,
            shadow: { sizeX: 1.5, sizeY: 1.5, offsetZ: - 0.15, alpha: 0.35 },
            mass: 1,
            soundName: 'bowlingBall'
        })

        // Reset function
        this.bowling.reset = () =>
        {
            for(const _pin of this.bowling.pins.items)
            {
                _pin.collision.reset()
            }
            this.bowling.ball.collision.reset()
        }

        // Reset area at specified position
        this.bowling.resetArea = this.areas.add({
            position: new THREE.Vector2(-28.5, -33.8),
            halfExtents: new THREE.Vector2(2, 2)
        })
        this.bowling.resetArea.on('interact', () =>
        {
            this.bowling.reset()
        })

        // Reset label
        this.resources.items.areaResetTexture.magFilter = THREE.NearestFilter
        this.resources.items.areaResetTexture.minFilter = THREE.LinearFilter
        this.bowling.areaLabelMesh = new THREE.Mesh(
            new THREE.PlaneGeometry(2, 0.5), 
            new THREE.MeshBasicMaterial({ transparent: true, depthWrite: false, color: 0xffffff, alphaMap: this.resources.items.areaResetTexture })
        )
        this.bowling.areaLabelMesh.position.x = -28.5
        this.bowling.areaLabelMesh.position.y = -33.8
        this.bowling.areaLabelMesh.matrixAutoUpdate = false
        this.bowling.areaLabelMesh.updateMatrix()
        this.container.add(this.bowling.areaLabelMesh)
    }

    setCollectibles()
    {
        // First, extract positions from test_cube objects in GLB for debugging
        const findAllObjectsByName = (object, namePattern, results = []) => {
            const objectName = object.name ? object.name.toLowerCase() : ''
            if (objectName.includes(namePattern.toLowerCase())) {
                results.push(object)
            }
            for (const child of object.children) {
                findAllObjectsByName(child, namePattern, results)
            }
            return results
        }

        // Find test_cube objects (supports test_cube, test_cube.001, test_cube.002, etc.)
        const testCubeObjects = findAllObjectsByName(this.resources.items.introStaticBase.scene, 'test_cube')
        
        // Extract positions and log for debugging
        const spawnPositions = []
        console.log(`[IntroSection] ===== DEBUG: Found ${testCubeObjects.length} test_cube objects (test_cube, test_cube.001, test_cube.002, etc.) =====`)
        
        for (let i = 0; i < testCubeObjects.length; i++) {
            const testCube = testCubeObjects[i]
            const worldPosition = new THREE.Vector3()
            testCube.getWorldPosition(worldPosition)
            spawnPositions.push(new THREE.Vector3(worldPosition.x, worldPosition.y, worldPosition.z))
            console.log(`[IntroSection] test_cube #${i + 1} position: new THREE.Vector3(${worldPosition.x.toFixed(2)}, ${worldPosition.y.toFixed(2)}, ${worldPosition.z.toFixed(2)})`)
        }
        
        console.log('[IntroSection] ===== All apple spawn positions (copy-paste ready): =====')
        const positionsString = spawnPositions.map((p, i) => 
            `    new THREE.Vector3(${p.x.toFixed(2)}, ${p.y.toFixed(2)}, ${p.z.toFixed(2)})${i < spawnPositions.length - 1 ? ',' : ''}`
        ).join('\n')
        console.log(positionsString)
        console.log('[IntroSection] ===== End positions =====')

        // If no test_cube found, use empty array (will be populated from console output)
        if (spawnPositions.length === 0) {
            console.warn('[IntroSection] No test_cube objects found. Add positions manually to spawnPositions array.')
            return
        }

        // Create apple meshes at each position
        for (const position of spawnPositions) {
            // Clone the apple model
            const mesh = this.resources.items.appleModel.scene.clone(true)
            mesh.scale.set(0.65, 0.65, 0.65)
            mesh.position.copy(position)
            mesh.rotation.set(Math.PI * 0.5, 0, 0)
            mesh.castShadow = false
            mesh.receiveShadow = false
            
            // Add shadow with sphere geometry (10x smaller)
            const shadowGeometry = new THREE.SphereGeometry(0.1, 16, 16)
            const shadowMaterial = this.shadows.materials.base.clone()
            const shadowMesh = new THREE.Mesh(shadowGeometry, shadowMaterial)
            shadowMesh.position.set(position.x, position.y, 0.001)
            shadowMesh.scale.set(0.14, 0.14, 0.1) // Flatten the sphere for shadow effect (10x smaller)
            this.shadows.container.add(shadowMesh)
            
            const shadow = {
                mesh: shadowMesh,
                reference: mesh,
                offsetZ: -0.35,
                alpha: 0.7
            }
            
            this.container.add(mesh)
            
            // Store collectible target
            this.collectibleTargets.push({
                mesh,
                position,
                collected: false,
                shadow
            })
        }

        // Create UI counter and reveal it
        this.createCollectibleCounter()
        const counterElement = document.querySelector('.collectible-counter')
        if(counterElement)
        {
            counterElement.classList.add('is-visible')
        }

        // Collection detection and rotation in time tick
        this.time.on('tick', () => {
            if (!this.car || !this.car.position) return

            for (const target of this.collectibleTargets) {
                if (target.collected) continue

                // Rotate apple slowly around Y axis (10x slower)
                target.mesh.rotation.y += this.time.delta * 0.005

                // Update shadow position
                if (target.shadow && target.shadow.mesh) {
                    const z = Math.max(target.mesh.position.z + target.shadow.offsetZ, 0)
                    const sunOffset = this.shadows.sun.vector.clone().multiplyScalar(z)
                    target.shadow.mesh.position.x = target.mesh.position.x + sunOffset.x
                    target.shadow.mesh.position.y = target.mesh.position.y + sunOffset.y
                    
                    // Update shadow alpha based on distance
                    let alpha = (this.shadows.maxDistance - z) / this.shadows.maxDistance
                    alpha = Math.min(Math.max(alpha, 0), 1)
                    alpha = Math.pow(alpha, this.shadows.distancePower)
                    target.shadow.mesh.material.uniforms.uAlpha.value = this.shadows.alpha * target.shadow.alpha * alpha
                }

                const distance = this.car.position.distanceTo(target.position)
                
                if (distance < this.collectibleThreshold) {
                    target.collected = true
                    target.mesh.visible = false
                    if (target.shadow && target.shadow.mesh) {
                        target.shadow.mesh.visible = false
                    }
                    
                    this.collectibleCounter++
                    this.updateCollectibleCounter()
                    
                    // Play sound immediately on collect
                    if (this.sounds) {
                        this.sounds.play('appleCollect', 0)
                    }
                    
                    console.log(`[IntroSection] Wumpa fruit collected! Total: ${this.collectibleCounter}/${this.collectibleTargets.length}`)

                    // If all fruits are collected, open Crash Team Racing on RetroGames
                    if (!this.collectibleRewardOpened && this.collectibleCounter >= this.collectibleTargets.length) {
                        this.collectibleRewardOpened = true
                        try {
                            window.open('https://www.retrogames.cc/psx-games/crash-team-racing.html', '_blank')
                            console.log('[IntroSection] All Wumpa collected! Opening Crash Team Racing reward page.')
                        } catch (error) {
                            console.warn('[IntroSection] Failed to open reward URL:', error)
                        }
                    }
                }
            }
        })
    }

    createCollectibleCounter()
    {
        const counterElement = document.querySelector('.js-wumpa-counter')
        if (counterElement) {
            counterElement.innerHTML = `Wumpa Fruit: <strong>0/${this.collectibleTargets.length}</strong>`
        }
    }

    updateCollectibleCounter()
    {
        const counterElement = document.querySelector('.js-wumpa-counter')
        if (counterElement) {
            counterElement.innerHTML = `Wumpa Fruit: <strong>${this.collectibleCounter}/${this.collectibleTargets.length}</strong>`
        }
    }

    setTexts()
    {
        // Helper function to find object by name recursively
        const findObjectByName = (object, namePattern, result = null) => {
            const objectName = object.name ? object.name.toLowerCase() : ''
            if (objectName.includes(namePattern.toLowerCase())) {
                return object
            }
            for (const child of object.children) {
                const found = findObjectByName(child, namePattern, result)
                if (found) return found
            }
            return null
        }

        // Helper function to create text at a child location
        const createTextAtChild = (childName, text, fontSize = 200) => {
            const childObject = findObjectByName(this.resources.items.introStaticBase.scene, childName)
            
            if (!childObject) {
                console.warn(`[IntroSection] ${childName} object not found in introStaticBase scene`)
                return
            }

            // Get world position (x, y only, ignore z)
            const worldPosition = new THREE.Vector3()
            childObject.getWorldPosition(worldPosition)
            console.log(`[IntroSection] Found ${childName} at position:`, worldPosition)

            // Create canvas for text texture
            const canvas = document.createElement('canvas')
            const context = canvas.getContext('2d')
            canvas.width = 2048
            canvas.height = 512
            
            // Draw white text
            context.fillStyle = '#ffffff'
            context.font = `bold ${fontSize}px Arial`
            context.textAlign = 'center'
            context.textBaseline = 'middle'
            context.fillText(text, canvas.width / 2, canvas.height / 2)
            
            // Create texture from canvas
            const texture = new THREE.CanvasTexture(canvas)
            texture.needsUpdate = true
            
            // Create material
            const material = new THREE.MeshBasicMaterial({
                map: texture,
                transparent: true,
                side: THREE.DoubleSide
            })
            
            // Create plane geometry
            const geometry = new THREE.PlaneGeometry(10, 5)
            const mesh = new THREE.Mesh(geometry, material)
            
            // Position at child location (x, y only, ignore z)
            mesh.position.set(worldPosition.x, worldPosition.y, 0)
            
            this.container.add(mesh)
        }

        // Add texts at child locations - just add more lines here!
        createTextAtChild('hello_text', 'Hello! This is')

        // Place paper7 at the position of child 'text_2'
        const text2Object = findObjectByName(this.resources.items.introStaticBase.scene, 'text_2')
        if (text2Object && this.papers && this.papers.paper7 && this.papers.paper7.mesh) {
            const worldPosition = new THREE.Vector3()
            text2Object.getWorldPosition(worldPosition)
            console.log('[IntroSection] Found text_2 at position for paper7:', worldPosition)

            // Reuse paper7 geometry/material
            const paperGeometry = this.papers.paper7.geometry
            const paperMaterial = this.papers.paper7.material
            const paperMesh = new THREE.Mesh(paperGeometry, paperMaterial)

            // Match x/y (slightly lower), keep same small z offset
            paperMesh.position.set(worldPosition.x+3, worldPosition.y - 3.5, this.papers.paper7.mesh.position.z)
            // Scale up by 2
            paperMesh.scale.set(1, 2, 2)
            paperMesh.rotation.copy(this.papers.paper7.mesh.rotation)

            this.container.add(paperMesh)
        } else {
            console.warn('[IntroSection] text_2 object or paper7 data not found')
        }
    }
}
